/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Typography,
  Card,
  message,
  Input,
  Button,
  Space,
  Spin,
  Tooltip,
  DatePicker,
  Select,
  Progress,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  ClearOutlined,
  FileAddOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Heading from "../../components/Heading";
import urls from "../../api/urls";
import API from "../../api";
import constants from "../../constants/constants";
import "./style.scss";

const { Text } = Typography;
const { Option } = Select;

const { CP_ADMIN, CP_OPERATOR } = constants.ROLES;

const DeliveryInvoiceList = () => {
  const userinfo = JSON.parse(localStorage.getItem("userinfo")) || {};
  const userId = JSON.parse(localStorage.getItem("userId")) || {};
  const userRole = userinfo?.roles?.[0];
  const isCPUser = [CP_ADMIN, CP_OPERATOR].includes(userRole);

  const cpHubId = isCPUser ? userId?.premises?.parentId : null;
  const cpHubName = isCPUser ? userId?.premises?.parentPremiseName : null;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [apiColumns, setApiColumns] = useState([]);
  const [tableConfig, setTableConfig] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const savedMonth = localStorage.getItem("deliveryInvoiceSelectedMonth");
    return savedMonth ? moment(savedMonth) : moment();
  });

  useEffect(() => {
    localStorage.setItem(
      "deliveryInvoiceSelectedMonth",
      selectedMonth.toISOString(),
    );
  }, [selectedMonth]);

  const [selectedHub, setSelectedHub] = useState(isCPUser ? cpHubId : null);
  const [refreshingRows, setRefreshingRows] = useState(new Set());
  const refreshingRowsRef = useRef(new Set());
  useEffect(() => {
    refreshingRowsRef.current = refreshingRows;
  }, [refreshingRows]);
  const [hubOptions, setHubOptions] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });

  const [columnFilters, setColumnFilters] = useState({});
  const [generatingReportRows, setGeneratingReportRows] = useState(new Set());
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [liveTrackingRows, setLiveTrackingRows] = useState(new Set());
  const apiCallInProgress = useRef(false);
  const sseConnections = useRef({});
  const sseRetryCount = useRef({});

  const fetchHubList = async () => {
    if (isCPUser) {
      // CP users are locked to their own hub — no API call needed
      if (cpHubId && cpHubName) {
        setHubOptions([{ value: cpHubId, label: cpHubName }]);
      }
      return;
    }
    try {
      const res = await API.Api(urls.GET_HUB_LIST).get();
      if (res?.status) {
        const hubs = Array.isArray(res.response.data)
          ? res.response.data
          : res.response.data?.data || [];
        setHubOptions(hubs.map((h) => ({ value: h.RoID, label: h.RoName })));
      }
    } catch (err) {
      console.error("Error fetching hubs:", err);
      message.error("Error fetching hubs");
    }
  };

  const fetchInvoiceListData = async (
    page = 1,
    pageSize = 10,
    currentFilters = columnFilters,
  ) => {
    if (isCPUser) {
      return;
    }

    if (apiCallInProgress.current) {
      return;
    }

    setLoading(true);
    apiCallInProgress.current = true;

    try {
      const queryParams = {
        page: page,
        limit: pageSize,
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      };

      if (selectedHub) {
        queryParams.hubId = selectedHub;
      }

      // Pass refId for CP_ADMIN and CP_OPERATOR roles
      if (isCPUser && userinfo?.spId) {
        queryParams.refId = userinfo.spId;
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && String(value).trim() !== "") {
          queryParams[key] = String(value).trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();
      const apiUrl = urls.DELIVERY_INVOICE_LIST + "?" + queryString;

      const response = await API.Api(apiUrl).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (response?.status) {
        if (response?.response?.data?.status === "success") {
          const resData = response.response.data;
          setData(resData.data || []);

          if (resData.pagination) {
            setPagination({
              current: page,
              pageSize: pageSize,
              total: resData.pagination.totalCount || 0,
              showSizeChanger: true,
              pageSizeOptions: resData.pagination.pageSizeOptions?.map(
                String,
              ) || ["10", "20", "50", "100"],
            });
          }

          if (resData.tableConfig) {
            setTableConfig(resData.tableConfig);
          }

          if (resData.columns) {
            setApiColumns(resData.columns);
          }
        } else {
          message.error(
            response?.response?.data?.message ||
            "Failed to fetch invoice list data",
          );
        }
      }
    } catch (error) {
      console.error("Error fetching invoice list data:", error);
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  };

  const getColumnSearchProps = (col) => {
    const dataIndex = col.key;
    const title = col.title;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${title}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              handleSearch(dataIndex, selectedKeys[0]);
              confirm();
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(dataIndex, selectedKeys[0]);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                handleSearch(dataIndex, "");
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  const handlePrint = async (record) => {
    const invoiceNo = record.invoiceNumber || record.invoiceNo;
    const invoiceUrl = record.invoiceUrl;

    if (!invoiceUrl) {
      message.error("Invoice URL not found");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(invoiceUrl);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${invoiceNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success("Invoice download started");
    } catch (err) {
      console.error("Download failed:", err);
      message.error("Failed to download invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (field, value) => {
    const newFilters = { ...columnFilters, [field]: value };
    setColumnFilters(newFilters);
    fetchInvoiceListData(1, pagination.pageSize, newFilters);
  };

  const handleGenerateSingleInvoice = async (record, skipLoading = false) => {
    const userName = localStorage.getItem("userName");

    try {
      if (!skipLoading) setLoading(true);
      const payload = {
        refId: record.refId,
        companyId: String(record.companyId),
        companyName: record.companyName,
        seriesCode: record.seriesCode,
        createdBy: userName,
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      };

      const response = await API.Api(
        urls.GENERATE_DELIVERY_INVOICE_ROW,
      ).postCustomHeader(payload, {
        kid: "1",
        authtype: "jwt",
      });

      console.log("[GenerateInvoice] payload:", payload);
      console.log("[GenerateInvoice] response:", response);

      if (response?.status) {
        if (response?.response?.data?.status === "success") {
          message.success("Invoice generated successfully");
          fetchInvoiceListData(pagination.current, pagination.pageSize);
        } else {
          message.error(
            response?.response?.data?.message || "Failed to generate invoice",
          );
        }
      } else {
        const httpStatus = response?.response?.status;
        const serverMsg = response?.response?.data?.message
          || response?.response?.data?.error
          || response?.response?.data;
        console.error("[GenerateInvoice] API error:", httpStatus, serverMsg);
        message.error(
          typeof serverMsg === "string"
            ? serverMsg
            : `Failed to generate invoice (HTTP ${httpStatus || "unknown"})`,
        );
      }
    } catch (err) {
      console.error("Error generating invoice:", err);
      message.error("Something went wrong. Please try again.");
    } finally {
      if (!skipLoading) setLoading(false);
    }
  };

  const handleGenerateReportRow = async (record) => {
    if (generatingReportRows.has(record.companyId)) {
      return;
    }

    setGeneratingReportRows((prev) => new Set([...prev, record.companyId]));

    try {
      const payload = {
        refId: record.refId,
        companyId: Number(record.companyId),
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      };

      const response = await API.Api(
        urls.DELIVERY_INVOICE_REPORT_TRIGGER_LAMBDA,
      ).postCustomHeader(payload, {
        kid: "1",
        authtype: "jwt",
      });

      if (response?.status) {
        if (response?.response?.data?.status === "success") {
          message.success(
            response.response.data.message || "Report generation started",
          );

          setLiveTrackingRows((prev) => new Set([...prev, String(record.companyId)]));
          fetchInvoiceListData(pagination.current, pagination.pageSize);
        } else {
          message.error(
            response?.response?.data?.message ||
            "Failed to start report generation",
          );
        }
      }
    } catch (err) {
      console.error("Error starting report generation:", err);
    } finally {
      setGeneratingReportRows((prev) => {
        const next = new Set(prev);
        next.delete(record.companyId);
        return next;
      });
    }
  };

  const handleBulkGenerateReports = async () => {
    const eligibleRows = data.filter((item) => {
      const isInProgress =
        liveTrackingRows.has(String(item.companyId)) ||
        item.reportJobStatus === "in_progress";
      const isCompleted =
        item.reportJobStatus === "completed" ||
        item.reportCompletionPct === 100;
      return !isInProgress && !isCompleted;
    });

    if (eligibleRows.length === 0) {
      message.warning("No pending or failed rows to generate reports for");
      return;
    }

    setBulkGenerating(true);
    try {
      const reportRequests = eligibleRows.map((item) => ({
        refId: item.refId,
        companyId: Number(item.companyId),
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      }));

      const response = await API.Api(
        urls.DELIVERY_INVOICE_REPORT_TRIGGER_LAMBDA_BULK,
      ).postCustomHeader({ reportRequests }, {
        kid: "1",
        authtype: "jwt",
      });

      if (response?.status) {
        if (response?.response?.data?.status === "success") {
          message.success(
            response.response.data.message ||
            `Bulk report generation started for ${eligibleRows.length} records`,
          );

          fetchInvoiceListData(pagination.current, pagination.pageSize);

          setLiveTrackingRows((prev) => {
            const next = new Set(prev);
            eligibleRows.forEach((item) => next.add(String(item.companyId)));
            return next;
          });
        } else {
          message.error(
            response?.response?.data?.message ||
            "Failed to start bulk report generation",
          );
        }
      }
    } catch (err) {
      console.error("Error starting bulk report generation:", err);
      message.error("An error occurred during bulk report generation");
    } finally {
      setBulkGenerating(false);
    }
  };

  const isCurrentMonth = selectedMonth.isSame(moment(), "month");

  const columns = useMemo(() => {
    const formattedApiColumns = apiColumns.map((col) => {
      const formattedCol = {
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        width: col.width || 150,
        sorter: col.sortable,
        ellipsis: true,
      };

      if (col.filter && col.filter.type === "search") {
        Object.assign(formattedCol, getColumnSearchProps(col));
      }

      if (col.render) {
        if (col.render.type === "datetime") {
          formattedCol.render = (text) =>
            text
              ? moment(text).format(col.render.format || "DD-MM-YYYY HH:mm:ss")
              : "-";
        } else if (col.key === "cancel" || col.key === "isCancel") {
          formattedCol.render = (text) => {
            return text;
          };
        }
      }

      return formattedCol;
    });

    // Action Column
    formattedApiColumns.push({
      title: "Action",
      key: "action",
      width: 280,
      fixed: "right",
      className: "action-column",
      render: (_, record) => {
        const isLiveTracking = liveTrackingRows.has(String(record.companyId));
        const isPending =
          !isLiveTracking &&
          (record.reportJobStatus === "pending" ||
            record.reportJobStatus?.toLowerCase() === "pending" ||
            !record.reportJobStatus);
        const isInProgress =
          isLiveTracking || record.reportJobStatus === "in_progress";
        const isFailed = !isLiveTracking && record.reportJobStatus === "failed";
        const isCompleted =
          !isLiveTracking &&
          (record.reportJobStatus === "completed" ||
            record.reportCompletionPct === 100);

        return (
          <div className="action-cell">
            <Space size={6} className="action-buttons">
              {!isCPUser && (isPending || isFailed) && !isInProgress && !isCompleted && (
                <Tooltip title="Generate Report">
                  <Button
                    className="action-btn report-btn"
                    icon={<FileAddOutlined />}
                    onClick={() => handleGenerateReportRow(record)}
                    loading={generatingReportRows.has(record.companyId)}
                    size="small"
                  >
                    Report
                  </Button>
                </Tooltip>
              )}
              {isCompleted && record.isInvoiceCreated && (
                <Tooltip title="Download Invoice">
                  <Button
                    className="action-btn download-btn"
                    icon={<DownloadOutlined />}
                    onClick={() => handlePrint(record)}
                    size="small"
                  >
                    Download
                  </Button>
                </Tooltip>
              )}
              {isCompleted && !record.isInvoiceCreated && !isCPUser && (
                <Tooltip title="Generate Invoice">
                  <Button
                    className="action-btn generate-btn"
                    icon={<FileAddOutlined />}
                    onClick={() => handleGenerateSingleInvoice(record)}
                    size="small"
                  >
                    Invoice
                  </Button>
                </Tooltip>
              )}
              {isCompleted && record.isInvoiceCreated && isCurrentMonth && !isCPUser && (
                <Tooltip title="Refresh Invoice">
                  <Button
                    className="action-btn generate-btn"
                    icon={<ReloadOutlined />}
                    onClick={() => handleRefreshSingleInvoice(record)}
                    loading={refreshingRows.has(String(record.companyId))}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Tooltip>
              )}
            </Space>

            {(isInProgress || isFailed) && (
              <div className="status-container">
                <div className="status-label">
                  <span
                    className={`status-badge status-${isFailed ? "failed" : isInProgress ? "progress" : "pending"}`}
                  >
                    <span className="status-dot" />
                    {isInProgress
                      ? "In Progress"
                      : "Failed"}
                  </span>
                  <Text className="pct-text">
                    {record.reportCompletionPct || 0}%
                  </Text>
                </div>
                <Progress
                  percent={record.reportCompletionPct || 0}
                  size="small"
                  strokeWidth={4}
                  status={
                    isFailed ? "exception" : isInProgress ? "active" : "normal"
                  }
                  showInfo={false}
                />
              </div>
            )}
          </div>
        );
      },
    });

    return formattedApiColumns;
  }, [apiColumns, columnFilters, pagination, generatingReportRows, data, liveTrackingRows, refreshingRows, isCurrentMonth]);

  const handleRefreshSingleInvoice = async (record) => {
    const companyId = String(record.companyId);
    if (refreshingRows.has(companyId)) return;

    setRefreshingRows((prev) => new Set([...prev, companyId]));
    message.loading({ content: `Refreshing invoice for ${record.companyName}...`, key: `refresh-${companyId}` });

    try {
      const payload = {
        refId: record.refId,
        companyId: Number(record.companyId),
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      };

      const triggerRes = await API.Api(
        urls.DELIVERY_INVOICE_REPORT_TRIGGER_LAMBDA,
      ).postCustomHeader(payload, {
        kid: "1",
        authtype: "jwt",
      });

      if (!triggerRes?.status || triggerRes?.response?.data?.status !== "success") {
        throw new Error(triggerRes?.response?.data?.message || "Failed to trigger report");
      }

      setLiveTrackingRows((prev) => new Set([...prev, companyId]));
    } catch (err) {
      console.error("Single Refresh failed:", err);
      message.error({ content: `Refresh failed: ${err.message}`, key: `refresh-${companyId}` });
      setRefreshingRows((prev) => {
        const next = new Set(prev);
        next.delete(companyId);
        return next;
      });
    }
  };

  const handleTableChange = (pag, filters) => {
    const formattedFilters = { ...columnFilters };
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        [formattedFilters[key]] = filters[key];
      } else {
        delete formattedFilters[key];
      }
    });

    setColumnFilters(formattedFilters);
    fetchInvoiceListData(pag.current, pag.pageSize, formattedFilters);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const queryParams = {
        export: true,
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      };

      if (selectedHub) {
        queryParams.hubId = selectedHub;
      }

      if (isCPUser && userinfo?.spId) {
        queryParams.refId = userinfo.spId;
      }

      const queryString = new URLSearchParams(queryParams).toString();

      const userData =
        JSON.parse(localStorage.getItem("userinfo")) ||
        JSON.parse(sessionStorage.getItem("userinfo"));

      const axiosResponse = await axios.get(
        urls.DELIVERY_INVOICE_LIST + "?" + queryString,
        {
          responseType: "arraybuffer",
          headers: {
            Authorization: "Bearer " + userData?.accessToken,
            kid: "1",
            authtype: "jwt",
          },
        },
      );

      if (axiosResponse?.status === 200 && axiosResponse?.data) {
        const blob = new Blob([axiosResponse.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Delivery_Invoice_List_${queryParams.year}_${queryParams.month}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("Export successful");
      } else {
        message.error("Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceListData(1, pagination.pageSize);
  }, [selectedMonth, selectedHub]);

  // Auto-start SSE for rows that are already in_progress
  useEffect(() => {
    const inProgressIds = data
      .filter((item) => item.reportJobStatus === "in_progress")
      .map((item) => String(item.companyId));

    if (inProgressIds.length > 0) {
      setLiveTrackingRows((prev) => {
        const next = new Set(prev);
        let changed = false;
        inProgressIds.forEach((id) => {
          if (!next.has(id)) {
            next.add(id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [data]);

  useEffect(() => {
    const trackingIds = Array.from(liveTrackingRows);

    Object.keys(sseConnections.current).forEach((id) => {
      if (!liveTrackingRows.has(id)) {
        sseConnections.current[id].close();
        delete sseConnections.current[id];
      }
    });

    trackingIds.forEach((id) => {
      if (!sseConnections.current[id]) {
        const userData =
          JSON.parse(localStorage.getItem("userinfo")) ||
          JSON.parse(sessionStorage.getItem("userinfo"));
        const token = userData?.accessToken || "";

        const queryParams = new URLSearchParams({
          companyId: id,
          year: selectedMonth.year(),
          month: selectedMonth.month() + 1,
          ...(token ? { token } : {}),
        }).toString();

        const sseUrl = `${urls.DELIVERY_INVOICE_REPORT_PROGRESS}?${queryParams}`;
        const es = new EventSource(sseUrl);

        es.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            const newPct = msg.report_completion_pct ?? msg.progress_pct ?? 0;

            delete sseRetryCount.current[id];

            setData((prev) =>
              prev.map((item) =>
                String(item.companyId) === id
                  ? {
                    ...item,
                    reportCompletionPct: newPct,
                    reportJobStatus: newPct >= 100 ? "completed" : "in_progress",
                  }
                  : item,
              ),
            );

            if (newPct >= 100) {
              es.close();
              delete sseConnections.current[id];
              delete sseRetryCount.current[id];

              if (refreshingRowsRef.current.has(id)) {
                setData((currentData) => {
                  const record = currentData.find((item) => String(item.companyId) === id);
                  if (record) {
                    handleGenerateSingleInvoice(record, true).finally(() => {
                      setRefreshingRows((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                      });
                      message.success({ content: `Invoice refreshed for ${record.companyName}`, key: `refresh-${id}` });
                    });
                  }
                  return currentData;
                });
              }

              setLiveTrackingRows((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              });
              fetchInvoiceListData(pagination.current, pagination.pageSize);
            }
          } catch (err) {
            console.error("SSE parse error:", err);
          }
        };

        es.onerror = (err) => {
          console.error(`[SSE] Error for companyId=${id}:`, err);
          es.close();
          delete sseConnections.current[id];

          const retries = sseRetryCount.current[id] || 0;
          if (retries < 3) {
            sseRetryCount.current[id] = retries + 1;
            const delay = 3000 * (retries + 1);
            setTimeout(() => {
              setLiveTrackingRows((prev) => {
                if (prev.has(id)) {
                  return new Set(prev);
                }
                return prev;
              });
            }, delay);
          } else {
            delete sseRetryCount.current[id];
            setLiveTrackingRows((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            fetchInvoiceListData(pagination.current, pagination.pageSize);
          }
        };

        sseConnections.current[id] = es;
      }
    });

    return () => {};
  }, [liveTrackingRows, selectedMonth]);

  useEffect(() => {
    return () => {
      Object.values(sseConnections.current).forEach((es) => es.close());
      sseConnections.current = {};
    };
  }, []);

  useEffect(() => {
    fetchHubList();
  }, []);

  return (
    <div className="delivery-invoice-list-container">
      <Spin spinning={loading}>
        <div className="report-header">
          <Heading title={tableConfig?.title || "Delivery Invoice List"} />
        </div>

        <Card className="report-card">
          <div className="filter-section">
            <div className="filter-group">
              <Select
                placeholder="Select Hub"
                style={{ width: 220 }}
                showSearch
                allowClear
                optionFilterProp="children"
                value={selectedHub}
                onChange={(value) => setSelectedHub(value || null)}
                disabled={isCPUser}
              >
                {hubOptions.map((hub) => (
                  <Option key={hub.value} value={hub.value}>
                    {hub.label}
                  </Option>
                ))}
              </Select>
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={(date) => setSelectedMonth(date)}
                format="MMMM YYYY"
                allowClear={false}
                style={{ width: 180 }}
                disabledDate={(current) =>
                  current && current > moment().endOf("month")
                }
              />
            </div>
            <div className="action-group">
              {!isCPUser && (() => {
                const hasBulkEligibleRows = data.some((item) => {
                  const isInProgress =
                    liveTrackingRows.has(String(item.companyId)) ||
                    item.reportJobStatus === "in_progress";
                  const isCompleted =
                    item.reportJobStatus === "completed" ||
                    item.reportCompletionPct === 100;
                  return !isInProgress && !isCompleted;
                });
                return hasBulkEligibleRows ? (
                  <Button
                    icon={<ThunderboltOutlined />}
                    type="primary"
                    onClick={handleBulkGenerateReports}
                    loading={bulkGenerating}
                    disabled={bulkGenerating}
                    className="toolbar-btn bulk-report-btn"
                  >
                    Generate All Reports
                  </Button>
                ) : null;
              })()}
              {tableConfig?.actionButtons
                ?.filter((btn) => btn.enabled)
                .map((btn) => {
                  if (btn.key === "export") {
                    return (
                      <Button
                        key={btn.key}
                        type={btn.type || "primary"}
                        icon={<DownloadOutlined />}
                        onClick={handleExport}
                        loading={loading}
                        className="toolbar-btn export-btn"
                      >
                        {btn.title || "Export"}
                      </Button>
                    );
                  }
                  return null;
                })}
              <div className="toolbar-divider" />
              <Button
                icon={<ReloadOutlined />}
                onClick={() =>
                  fetchInvoiceListData(pagination.current, pagination.pageSize)
                }
                loading={loading}
                className="toolbar-btn"
              >
                Refresh
              </Button>
              <Button
                icon={<ClearOutlined />}
                className="toolbar-btn"
                onClick={() => {
                  setColumnFilters({});
                  fetchInvoiceListData(1, pagination.pageSize, {});
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>

          {!isCPUser && (
            <Table
              columns={columns}
              dataSource={data}
              pagination={{
                ...pagination,
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={handleTableChange}
              rowKey={(record) => record.companyId || record.id || Math.random()}
              scroll={{ x: "max-content" }}
              bordered={false}
              size="middle"
              className="invoice-table"
            />
          )}
        </Card>
      </Spin>
    </div>
  );
};

export default DeliveryInvoiceList;
